import { Button, CalendarPicker, Badge } from '@appkit4/react-components';
import { useFormikContext } from 'formik';
import getWeekdayCount from 'helpers/getWeekdayCount';
import useLocale from 'locale/useLocale';
import { NewDeclarationFormValues } from 'pages/SubmissionFormWizard/SubmissionFormWizard';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import './PlannedDatesRow.scss';

interface NewDeclarationFormErrorFields {
    dateRanges: { [key: string]: DateRange };
}

interface DateRange {
    declarationId?: string;
    startDate: Date;
    endDate: Date;
}

export type PlannedDatesRowProps = {
    index: string;
    dateRange?: [startDate: Date, endDate: Date];
    disabledRanges: [Date, Date][];
    declarationId?: string;
};

const PlannedDatesRow = ({
    index,
    dateRange,
    disabledRanges,
    declarationId,
}: PlannedDatesRowProps) => {
    const { l } = useLocale();
    const { values, setFieldValue } =
        useFormikContext<NewDeclarationFormValues>();
    const { setFieldError, errors } =
        useFormikContext<NewDeclarationFormErrorFields>();
    const [numberOfDays, setNumberOfDays] = useState<string>('');

    const selectRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (selectRef.current) {
            selectRef.current.focus();
        }
    }, []);

    const today = new Date();
    const minDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    const maxDate = new Date();
    maxDate.setTime(today.getTime() + 366 * 24 * 60 * 60 * 1000);

    const filteredDisabledRanges = useMemo(() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return disabledRanges;
        }

        return disabledRanges.filter(
            ([disabledStartDate, disabledEndDate]) =>
                !(
                    disabledStartDate.getTime() === dateRange[0].getTime() &&
                    disabledEndDate.getTime() === dateRange[1].getTime()
                )
        );
    }, [disabledRanges, dateRange]);

    const getDatesBetween = (startDate: Date, endDate: Date): Date[] => {
        const dates = [];
        for (
            let currentDate = new Date(startDate);
            currentDate <= endDate;
            currentDate.setDate(currentDate.getDate() + 1)
        ) {
            dates.push(new Date(currentDate));
        }
        return dates;
    };

    const onDateChange = useCallback(
        (selectedDate: Date[]) => {
            if (
                selectedDate &&
                selectedDate.length === 2 &&
                selectedDate[0] &&
                selectedDate[1]
            ) {
                const datesInSelectedDateRange = getDatesBetween(
                    selectedDate[0],
                    selectedDate[1]
                );

                const simplifyDate = (date: Date) =>
                    new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                    );

                const dateInSelectedDateRangeIsDisabled =
                    datesInSelectedDateRange.some((date) =>
                        filteredDisabledRanges.some(
                            ([disabledStartDate, disabledEndDate]) => {
                                const simplifiedDate = simplifyDate(date);
                                return (
                                    simplifiedDate >=
                                        simplifyDate(disabledStartDate) &&
                                    simplifiedDate <=
                                        simplifyDate(disabledEndDate)
                                );
                            }
                        )
                    );

                if (dateInSelectedDateRangeIsDisabled) {
                    setFieldError(
                        `dateRanges.${index}`,
                        l('_plannedDatesRowDateRangesErrorMessage')
                    );
                } else {
                    setFieldError(`dateRanges.${index}`, undefined);

                    const updatedDateRanges = [...(values.dateRanges || [])];

                    const indexToUpdate = updatedDateRanges.findIndex(
                        (range, idx) => {
                            if (declarationId) {
                                return range.declarationId === declarationId;
                            } else {
                                return idx.toString() === index;
                            }
                        }
                    );

                    if (indexToUpdate !== -1) {
                        updatedDateRanges[indexToUpdate] = {
                            declarationId: declarationId || undefined,
                            startDate: selectedDate[0],
                            endDate: selectedDate[1],
                        };
                    } else {
                        updatedDateRanges.push({
                            declarationId: declarationId || undefined,
                            startDate: selectedDate[0],
                            endDate: selectedDate[1],
                        });
                    }

                    setFieldValue('dateRanges', updatedDateRanges, false);
                    setNumberOfDays(
                        getWeekdayCount(
                            selectedDate[0],
                            selectedDate[1]
                        ).toFixed(0)
                    );
                }
            }
        },
        [
            setFieldValue,
            setFieldError,
            values.dateRanges,
            index,
            filteredDisabledRanges,
            l,
            declarationId,
            setNumberOfDays,
            dateRange,
        ]
    );

    useEffect(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            setNumberOfDays(
                getWeekdayCount(dateRange[0], dateRange[1]).toFixed(0)
            );
        } else {
            setNumberOfDays('');
        }
    }, [dateRange]);

    const onRemoveDate = useCallback(
        (index: string) => {
            const indexToDelete = values.dateRanges?.findIndex((range, idx) => {
                return (
                    range.declarationId === index || idx.toString() === index
                );
            });

            if (indexToDelete !== -1) {
                if (index.length > 1) {
                    setFieldValue('declarationIdsToDelete', [
                        ...(values.declarationIdsToDelete || []),
                        index,
                    ]);
                }
                const updatedDateRanges = [...(values.dateRanges || [])].filter(
                    (range, idx) =>
                        range.declarationId !== index && idx !== indexToDelete
                );
                setFieldValue('dateRanges', updatedDateRanges);
            }
        },
        [
            setFieldValue,
            values.dateRanges,
            values.declarationIdsToDelete,
            index,
            declarationId,
        ]
    );

    const errorMessage =
        errors.dateRanges && errors.dateRanges[index]
            ? (errors.dateRanges[index] as string)
            : undefined;

    return (
        <div
            key={`${dateRange?.[0]?.getTime()}-${dateRange?.[1]?.getTime()}`}
            className="calender-picker-div"
        >
            <div
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
                tabIndex={0}
                ref={selectRef}
            >
                <CalendarPicker
                    key={`${index}-${dateRange?.[0]?.getTime()}-${dateRange?.[1]?.getTime()}`}
                    autoClose={false}
                    editable={false}
                    useCustomValidation={true}
                    onChange={(newSelectedDate: Date[]) =>
                        onDateChange(newSelectedDate)
                    }
                    selectRange={true}
                    placeholder="DD/MM/YYYY to DD/MM/YYYY"
                    locale="en"
                    fieldTitle={l('_dateRangeSubHeading')}
                    rangeConnecterFormat="to"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabledDays={[0, 6]}
                    disabledRanges={filteredDisabledRanges}
                    format="DD/MM/YYYY"
                    defaultValue={dateRange ? dateRange : undefined}
                />
            </div>
            <div className="badge-div ms-2">
                {(numberOfDays !== '' || (dateRange?.[0] && dateRange?.[1])) &&
                typeof errorMessage !== 'string' ? (
                    <Badge
                        value={
                            numberOfDays !== ''
                                ? `${numberOfDays} ${l(
                                      '_plannedDatesRowDaysSelected'
                                  )}`
                                : dateRange
                                ? `${(
                                      (dateRange[1].getTime() -
                                          dateRange[0].getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  ).toFixed(0)} ${l(
                                      '_plannedDatesRowDaysSelected'
                                  )}`
                                : ''
                        }
                        type={'primary'}
                    />
                ) : null}
            </div>
            {typeof errorMessage === 'string' && (
                <div className="error-message d-flex">
                    <span className="Appkit4-icon icon-error-outline ap-font-24 me-1" />
                    <div>{errorMessage}</div>
                </div>
            )}
            {values.dateRanges?.length && values.dateRanges?.length > 1 && (
                <Button
                    add
                    kind="text"
                    onClick={() => onRemoveDate(index)}
                    className="remove-button"
                    icon="icon-delete-outline"
                />
            )}
        </div>
    );
};

export default PlannedDatesRow;
